"use client";

import {
	createContext,
	useContext,
	useState,
	useCallback,
	useEffect,
	type ReactNode,
} from "react";

export type Consent = "all" | "essential" | null;

const STORAGE_KEY = "cookie_consent";

type ContextValue = {
	consent: Consent;
	hasHydrated: boolean;
	setConsent: (value: Consent) => void;
};

const ConsentContext = createContext<ContextValue | null>(null);

export function useConsent(): ContextValue {
	const ctx = useContext(ConsentContext);
	if (!ctx) {
		throw new Error("useConsent must be used within ConsentProvider");
	}
	return ctx;
}

export function ConsentProvider({ children }: { children: ReactNode }) {
	const [consent, setConsentState] = useState<Consent>(null);
	const [hasHydrated, setHasHydrated] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const stored = localStorage.getItem(STORAGE_KEY) as Consent | null;
		if (stored === "all" || stored === "essential") {
			setConsentState(stored);
		}
		setHasHydrated(true);
	}, []);

	const setConsent = useCallback((value: Consent) => {
		setConsentState(value);
		if (value && typeof window !== "undefined") {
			localStorage.setItem(STORAGE_KEY, value);
		}
	}, []);

	return (
		<ConsentContext.Provider value={{ consent, hasHydrated, setConsent }}>
			{children}
		</ConsentContext.Provider>
	);
}
